import React, {useEffect, useState} from "react";
import {
    Box,
    Paper,
    Grid,
    Button,
    Alert,
    CircularProgress,
    TextField,
    InputAdornment,
    useMediaQuery
} from "@mui/material";
import {useWaitListForm} from "./useWaitListForm";
import {Player} from "@lottiefiles/react-lottie-player";
import mailSend3 from "../../assets/mail_modal/main_animation_3.json";
import Image from "next/dist/client/legacy/image";
import alreadyRegisterImg from "../../assets/img/alreadyRegistered.png";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import QuestionnaireModal from "./questionnaire";
import {getConfig} from "pp-web";
import {isDealsEnabled} from "../../utils"
import { useSearchParams } from "next/navigation";

const config = getConfig(process.env.NEXT_PUBLIC_PROFILE)

export default function InlineWaitListForm(props) {
    const {
        title,
        actionText,
        message,
        persona = "Publisher",
        successMessage,
        successTitle,
        campaignName,
        alreadyMessage = "We will be in touch soon",
        alreadyTitle = "Thanks! You have already signed up",
        nameRequired = false,
        questions
    } = props;

    const {
        name,
        setName,
        email,
        setEmail,
        emailError,
        success,
        setSuccess,
        error,
        setError,
        errorCode,
        showLoader,
        handleForm
    } = useWaitListForm({persona, campaignName});
    const searchParams = useSearchParams();

    const isSmallScreen = useMediaQuery("(max-width:575px)");

    const sendMagicLink = (email) => {
        if (isDealsEnabled()) {
            fetch(`${config.siteApi.apiUrl}/crm/login`, {
                method: 'POST',
                body: JSON.stringify({
                    email,
                    name
                })
            });
        }
    }

    useEffect(() => {
        const errCode = searchParams.get('errorCode');
        if (errCode == 401) {
            setError('Your magic link has expired. Please enter your email below to request a new one.');
        }
    }, []);

    useEffect(() => {
        if (success || errorCode === 100) {
            sendMagicLink(email);
        }
    }, [success, errorCode]);

    return (
        <Box
            className="text-normal"
            sx={{
                backgroundColor: "rgba(0, 0, 0, 0.2)",
                padding: 1,
                margin: 1,
                borderRadius: 2,
                zIndex: 1300
            }}
        >
            <Paper
                sx={{
                    padding: {xs: '20px 10px', md: 3},
                    bgcolor: "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(10px)",
                    borderRadius: 2,
                    minHeight: 250,
                    width: {xs: '100%', lg: '700px'},
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <Grid container spacing={2}>
                    {error && (
                        <Grid item xs={12}>
                            <Alert severity="error">{error}</Alert>
                        </Grid>
                    )}
                    {!success && !errorCode && (
                        <>
                            <Grid item xs={12}><h2 style={{color: 'black'}}>{title}</h2></Grid>
                            <Grid item xs={12}><p style={{marginBottom: 8}}>{message}</p></Grid>
                        </>
                    )}
                    {nameRequired && (
                        <Grid item xs={12}>
                            <TextField
                                label="Your Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                variant="outlined"
                                fullWidth
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Grid container spacing={1} alignItems="center">
                            {!success && !errorCode && (
                                <>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Email"
                                            variant="outlined"
                                            fullWidth
                                            error={Boolean(emailError)}
                                            helperText={emailError}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            size={isSmallScreen && "small"}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px",
                                                    overflow: "hidden",
                                                    paddingRight: 0,
                                                    transition: "all 0.3s ease-in-out",
                                                },
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Button
                                                            className={`theme-btn ${showLoader ? '' : 'red-color'}`}
                                                            variant="contained"
                                                            onClick={handleForm}
                                                            disabled={showLoader}
                                                            sx={{
                                                                height: "100%",
                                                                borderRadius: "8px",
                                                                padding: {xs: "50px 4px", md: "50px 20px"},
                                                                color: "white",
                                                                fontWeight: "bold",
                                                                textTransform: "none",
                                                                transition: "all 0.3s ease",
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                alignItems: "center"
                                                            }}
                                                        >
                                                            {showLoader ? <CircularProgress
                                                                size={30}/> : actionText || "Join POLN"}
                                                            {(!isSmallScreen && !showLoader) && <KeyboardArrowRightIcon
                                                                style={{transform: 'translate(0,-1px)'}}/>}
                                                        </Button>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </>
                            )}
                            {success && !(errorCode || error) && (
                                <Grid item xs={12}>
                                    {
                                        success &&
                                        <Player
                                            src={mailSend3}
                                            autoplay
                                            keepLastFrame
                                            style={{width: 150, height: 150, padding: 0}}
                                        />
                                    }
                                    <h3 className="text-center">{successTitle}</h3>
                                    <p className="text-center" style={{padding: success && 0}}>{successMessage}</p>
                                </Grid>
                            )}
                            {errorCode === 100 && (
                                <Grid item xs={12}>
                                    {
                                        errorCode &&
                                        <p className="text-center" style={{paddingRight: 0}}>
                                            <Image src={alreadyRegisterImg} alt={'already-registered-img'} width={200}
                                                   height={200}/>
                                        </p>
                                    }
                                    <div className="d-flex flex-column justify-content-center">
                                        <h3 className="text-center">{alreadyTitle}</h3>
                                        <p className="text-secondary text-center p-0">There is already an account
                                            associated with
                                            <b className="text-danger"> {email || "this email"}</b></p>
                                        <p className="text-center p-0">Sit tight—your inbox is about to get a VIP pass
                                            to the hottest deals! 🎉✨ <br/><br/>Stay tuned!</p>
                                    </div>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>
            {questions &&
                <QuestionnaireModal questionaireModal={success}
                                    setQuestionaireModal={setSuccess}
                                    questions={questions}
                                    email={email}
                                    campaignName={campaignName} />
            }
        </Box>
    );
}
